import React from 'react';
import { ThemeProvider } from 'react-jss';
import { SocketIOProvider } from 'use-socketio';
import { StoreProvider } from 'easy-peasy';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import store from './store';

import { LocaleProvider } from './i18n';

import Wrapper from './components/common/Wrapper';

import Menu from './routes/Menu';
import HowToPlay from './routes/HowToPlay';
import Game from './routes/Game';

const theme = {
  colorPrimary: '#e3d7bb',       // tile surface (light parchment/cork)
  colorSecondary: '#d4b896',     // board surface
  colorTertiary: '#b09070',      // tile dots, subtle elements
  colorWall: '#2a1a0e',          // wall bars (near-black brown)
  colorCenter: '#241a10',        // center void plate
  colorPanel: '#2a1d13',         // sidebar/panel background
  colorFrame: '#4a3324',         // board frame (bevel needs it lighter than the panel)
  colorBorder: '#5a3a1a',        // subtle panel borders
  colorAccent: '#8b4513',        // warm brown accent
  colorText: '#f5e6c8',          // parchment text
  colorTextMuted: '#c4a070',     // muted text
  radius: '8px',
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Menu />,
  },
  {
    path: '/how-to-play',
    element: <HowToPlay />,
  },
  {
    path: '/game',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/game/:gameId',
    element: <Game />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

function App() {
  // Same-origin in every environment: nginx proxies /socket.io/ to the server
  // container in production (client/nginx.conf), and CRA's dev server does the
  // same in development (src/setupProxy.js). Building a host:port URL here
  // instead — which this used to do in dev — breaks the moment the app is
  // served through a reverse proxy, because the port it would name belongs to
  // the Docker host and not to the domain the browser is on.
  // REACT_APP_WEBSOCKET_URL still overrides, for a setup that genuinely has to
  // point somewhere else.
  const socketUrl = process.env.REACT_APP_WEBSOCKET_URL || undefined;

  return (
    <StoreProvider store={store}>
      <SocketIOProvider
        // socket.io only falls back to same-origin for null/undefined —
        // io("") parses to an empty host and never connects.
        url={socketUrl}
        opts={{ transports: ['websocket'], forceNode: true }}
      >
        <ThemeProvider theme={theme}>
          <LocaleProvider>
            <Wrapper>
              <RouterProvider router={router} />
            </Wrapper>
          </LocaleProvider>
        </ThemeProvider>
      </SocketIOProvider>
    </StoreProvider>
  );
}

export default App;
